/**
 * The Client Portal terms, rendered in full on screen.
 *
 * **The single source is `fig/terms/fig-terms-portal.md`** in the working folder, which is
 * checked by `terms-gate.py` (word count, reading level, sentence length, legalese, and that
 * no reading time is claimed). This file is that document, transcribed.
 *
 * ⚠️ `version` must equal `PortalTermsController.CurrentVersion` on the API, or acceptance is
 * a 409 by design — recording consent to terms that were never shown would be worse than no
 * record at all. Ship the API first, then this: the gap between them is the only window in
 * which acceptance fails.
 *
 * 2026-09-24: `2026-09-06` → `2026-09-24`. Rasel asked that the terms say plainly, at first
 * glance, that data is kept secure and encrypted. Checked on the live resources before it was
 * written (Neon AES-256 at rest + TLS required; storage HTTPS-only, TLS 1.2, encryption on; API
 * refuses plain HTTP). Every portal user is asked to accept again, which is the point.
 */
export const PORTAL_TERMS = {
  version: '2026-09-24',
  title: 'What you’re agreeing to',
  sections: [
    {
      heading: 'Who’s who',
      points: [
        'Your agency runs the AI agent that handled these calls. Fig is the tool they use to keep them.',
        'They invited you. They control your access and can remove it at any time.',
      ],
    },
    { heading: 'What you can see', points: ['Your organisation’s conversations. Nothing else, and nobody else’s.'] },
    {
      heading: 'What we record about you',
      points: [
        'Every time you open, read or play a conversation, we log that you did, and when.',
        'Your agency can see that log. It’s how they answer “who listened to this call”.',
      ],
    },
    {
      heading: 'Your data is secure and encrypted',
      points: [
        'Your conversations are stored securely and encrypted, both on the way to us and while we store them.',
        'We never sell your data.',
        'Your conversations are stored in Australia. Your email address is held by our sign-in provider in the United States.',
        'We keep recordings until your agency asks us to delete them. There’s no automatic expiry.',
      ],
    },
    {
      heading: 'What we’re not promising',
      points: ['Fig is new. Things will break sometimes.', 'We don’t promise it’s always up.'],
    },
    {
      heading: 'Questions and problems',
      points: ['About a call, a recording, or your access: ask your agency. They own the relationship.'],
    },
    { heading: 'Leaving', points: ['Ask your agency to remove your access. They can do it immediately.'] },
  ],
  closing:
    'Don’t agree? Then don’t sign in. Nothing bad happens — your agency keeps working the way it did before.',
} as const;
